import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, clubAPI, bookieAPI } from '../services/api';
import Footer from './Footer';
import './Dashboard.css';

function PersonalBettingCard() {
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');

  const handlePersonalBet = async () => {
    setLaunching(true);
    setError('');
    try {
      const data = await bookieAPI.getPersonalBetLaunchUrl();
      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        setError('Failed to get betting URL');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to launch personal betting');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="club-card small-feature-card" style={{ margin: 0 }}>
      <div className="club-card-header">
        <h3 className="dashboard-club-name" style={{ fontSize: '1rem' }}>Personal Betting</h3>
      </div>
      <div className="club-info" style={{ padding: '0.75rem 1rem' }}>
        <p style={{ color: '#999', fontSize: '0.75rem', marginBottom: 0, lineHeight: 1.4 }}>
          Place bets directly from your personal GRSBet account without selecting a club.
        </p>
        {error && <p style={{ color: '#ef4444', fontSize: '0.75rem', marginBottom: 0, marginTop: '0.5rem' }}>{error}</p>}
      </div>
      <div className="club-actions" style={{ padding: '0.75rem 1rem' }}>
        <button
          className="btn-view-club btn-inverse"
          onClick={handlePersonalBet}
          disabled={launching}
        >
          {launching ? 'Opening...' : 'Place Personal Bet'}
        </button>
      </div>
    </div>
  );
}

function Dashboard() {
  const [user, setUser] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showAll = searchParams.get('showAll') === 'true';

  const loadDashboard = async () => {
    try {
      console.log('Loading dashboard data...');

      const userData = await authAPI.getCurrentUser();
      console.log('User data loaded:', userData);
      setUser(userData.user);

      // Redirect to active club if user has one (unless showAll is set)
      if (userData.user.activeClubId && !showAll) {
        navigate(`/dashboard/${userData.user.activeClubId}`);
        return;
      }

      const clubData = await clubAPI.listClubs();
      console.log('Club data loaded:', clubData);
      setClubs(clubData.clubs || []);
    } catch (err) {
      console.error('Dashboard error:', err);
      console.error('Error details:', err.response?.data);
      console.error('Error status:', err.response?.status);

      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(`Failed to load dashboard data: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <img src="/images/beer-loading.gif" alt="Loading..." style={{ width: '80px', height: 'auto', marginBottom: '16px' }} />
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  const getStatusText = (status) => {
    const statusMap = {
      1: 'Active',
      2: 'Pending Bookie',
      3: 'Inactive',
    };
    return statusMap[status] || 'Unknown';
  };

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-logo">
            <img src="/logo.png" alt="Punt Club" />
            <h1>Punt Club</h1>
          </div>
          <div className="header-user">
            <span className="user-greeting">Welcome, {user?.firstname}!</span>
            <button onClick={handleLogout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
          <PersonalBettingCard compact />
          <div className="club-card small-feature-card" style={{ margin: 0 }}>
            <div className="club-card-header">
              <h3 className="dashboard-club-name" style={{ fontSize: '1rem' }}>Lotto Syndicates</h3>
            </div>
            <div className="club-info" style={{ padding: '0.75rem 1rem' }}>
              <p style={{ color: '#999', fontSize: '0.75rem', marginBottom: 0, lineHeight: 1.4 }}>
                Join a lottery syndicate. Buy shares in upcoming draws and split the winnings.
              </p>
            </div>
            <div className="club-actions" style={{ padding: '0.75rem 1rem' }}>
              <button
                className="btn-view-club btn-inverse"
                onClick={() => navigate('/lottery')}
              >
                View Lotto Syndicates
              </button>
            </div>
          </div>
        </div>

        {clubs.length === 0 ? (
          <div className="no-clubs-message">
            <h2>You haven't joined any clubs yet</h2>
            <p>Create a club or wait for an invitation to get started!</p>
            <button
              className="btn-warning"
              onClick={() => navigate('/register/club?_si=0')}
            >
              Create Your First Club
            </button>
          </div>
        ) : (
          <>
            <div className="clubs-header">
              <h2>Your Clubs</h2>
              <button
                className="btn-create-club"
                onClick={() => navigate('/register/club?_si=0')}
              >
                + Create New Club
              </button>
            </div>

            <div className="clubs-grid">
              {clubs.map((club) => (
                <div key={club.id} className="club-card">
                  <div className="club-card-header">
                    <h3 className="dashboard-club-name">{club.name}</h3>
                    {club.isCaptain && (
                      <span className="captain-badge">Captain</span>
                    )}
                  </div>

                  <div className="club-dashboard">
                    <div className="dashboard-stat">
                      <h6>Balance</h6>
                      <div className="stat-value">$0.00</div>
                    </div>
                    <div className="dashboard-stat">
                      <h6>ROI</h6>
                      <div className="stat-value">{club.roiCache}%</div>
                    </div>
                    <div className="dashboard-stat">
                      <h6>Wagered</h6>
                      <div className="stat-value">$0.00</div>
                    </div>
                    <div className="dashboard-stat">
                      <h6>Returned</h6>
                      <div className="stat-value">$0.00</div>
                    </div>
                  </div>

                  <div className="club-info">
                    <div className="info-row">
                      <span className="info-label">Status:</span>
                      <span className={`status-badge status-${club.status}`}>
                        {getStatusText(club.status)}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Club ID:</span>
                      <span className="info-value">#{club.id}</span>
                    </div>
                  </div>

                  <div className="club-actions">
                    {club.status === 2 ? (
                      <button
                        className="btn-link-bookie"
                        onClick={() => navigate(`/register/link?clubId=${club.id}`)}
                      >
                        Link Bookie Account
                      </button>
                    ) : (
                      <button
                        className="btn-view-club"
                        onClick={() => navigate(`/dashboard/${club.id}`)}
                      >
                        View Dashboard
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Dashboard;
